import React from "react";
import { Select } from "antd";
import "./common.scss";
import { ReactComponent as SearchIcon } from "../../Assets/Icons/searchIconGrey.svg";
import { ReactComponent as VerticalLine } from "../../Assets/Icons/verticalLine.svg";
import clubIcon from "../../Assets/Icons/clubIcon.svg";

const Selector = ({
  label,
  labelClassName,
  divClassName,
  selectClassName,
  errorClassName,
  flexClassName,
  options,
  onChange,
  suffixIcon,
  placeholder,
  defaultValue,
  value,
  handleBlur,
  error,
  touched,
  selectFundraiser,
  multiple,
  showIcon,
  disabled,
  onClick,
  onDeselect,
  mode, // <-- add mode prop
  ...rest
}) => {
  // Prepare class names and add cursor-not-allowed when disabled
  const disabledClasses = disabled ? "pointer-events-none cursor-not-allowed opacity-70" : "";
  const combinedSelectClassName = `${selectClassName} ${disabledClasses}`;

  // Normalize incoming value so null/undefined/empty do not render as the string "null"/"undefined"
  // Ant Design expects undefined for no-selection; stringify only meaningful values
  const normalizedValue = Array.isArray(value)
    ? value
      .filter((v) => v !== null && v !== undefined && v !== "")
      .map((v) => String(v))
    : (value === "" || value === null || value === undefined ? undefined : String(value));

  // Add a wrapper div that will handle click events when disabled
  const WrapperComponent = ({ children }) => {
    if (disabled) {
      return (
        <div className="relative w-full cursor-not-allowed">
          <div className="absolute inset-0 z-10"></div>
          {children}
        </div>
      );
    }
    return <>{children}</>;
  };

  // Helper to render options as children for tags mode
  const renderOptions = () => {
    if (!options) return null;
    return options.map((opt) => (
      <Select.Option key={String(opt.value)} value={String(opt.value)}>
        {opt.label}
      </Select.Option>
    ));
  };

  // Debug: help trace value/options issues in selects
  try {
    if (process.env.NODE_ENV !== 'production') {
      // Only log when something looks off
      const valPreview = Array.isArray(value) ? value : [value];
      // eslint-disable-next-line no-console
      console.debug('[Selector] label:', label, {
        mode,
        multiple,
        valueType: Array.isArray(value) ? 'array' : typeof value,
        valuePreview: valPreview?.slice?.(0, 5),
        optionsCount: Array.isArray(options) ? options.length : 0,
        firstOptions: (options || []).slice(0, 3).map(o => ({ v: String(o.value), l: o.label }))
      });
    }
  } catch (_) { }

  return (
    <div className={divClassName}>
      <p className={labelClassName}>{label}</p>
      <div className={`flex flex-col ${flexClassName}`}>
        {selectFundraiser ? (
          <WrapperComponent>
            <Select
              value={normalizedValue}
              placeholder={
                <div className="pl-3 flex items-center gap-2 font-poppins font-[400] text-[12px] md:text-[14px]">
                  <SearchIcon className="w-[12px] sm:w-[15px]" />
                  {placeholder}
                </div>
              }
              className={combinedSelectClassName}
              onChange={disabled ? null : onChange}
              onDeselect={disabled ? null : onDeselect}
              onBlur={disabled ? null : handleBlur}
              suffixIcon={
                <div className="flex gap-2 items-center">
                  <VerticalLine className="py-1 mr-2" />
                  {suffixIcon}
                </div>
              }
              mode={mode ? mode : (multiple === true ? "multiple" : undefined)}
              defaultValue={defaultValue}
              open={disabled ? false : undefined}
              onMouseDown={(e) => {
                if (disabled) {
                  e.preventDefault();
                  e.stopPropagation();
                  return;
                }
                if (onClick) onClick(e);
              }}
              // Only use options prop if not tags mode
              {...(mode === "tags"
                ? {}
                : { options: (options || []).map((o) => ({ ...o, value: String(o.value) })) })}
              {...rest}
            >
              {mode === "tags" && renderOptions()}
            </Select>
          </WrapperComponent>
        ) : showIcon ? (
          <WrapperComponent>
            <div className="relative flex min-h-[35px] sm:min-h-[45px] md:min-h-[50px]">
              <div
                className={`flex justify-center items-center ${"w-[15%] sm:w-[12%] bg-[#e1e1e1] rounded-tl-[10px] rounded-bl-[10px] border-none"}`}
              >
                <img src={clubIcon} className="w-5 h-5 sm:w-7 sm:h-7 object-contain" alt="icon"></img>
              </div>

              <Select
                value={normalizedValue}
                placeholder={
                  <div className="pl-3 flex items-center gap-2 font-poppins font-[400] text-[12px] md:text-[14px]">
                    <SearchIcon className="w-[12px] sm:w-[15px]" />
                    {placeholder}
                  </div>
                }
                className={combinedSelectClassName}
                onChange={disabled ? null : onChange}
                onDeselect={disabled ? null : onDeselect}
                onBlur={disabled ? null : handleBlur}
                suffixIcon={
                  <div className="flex gap-2 items-center">
                    <VerticalLine className="py-1 mr-2" />
                    {suffixIcon}
                  </div>
                }
                mode={mode ? mode : (multiple === true ? "multiple" : undefined)}
                defaultValue={defaultValue}
                open={disabled ? false : undefined}
                onMouseDown={(e) => {
                  if (disabled) {
                    e.preventDefault();
                    e.stopPropagation();
                    return;
                  }
                  if (onClick) onClick(e);
                }}
                {...(mode === "tags"
                  ? {}
                  : { options: (options || []).map((o) => ({ ...o, value: String(o.value) })) })}
                {...rest}
              >
                {mode === "tags" && renderOptions()}
              </Select>
            </div>
          </WrapperComponent>
        ) : (
          <WrapperComponent>
            <Select
              value={normalizedValue}
              placeholder={
                <div className="pl-3 font-poppins font-[400] text-[12px] md:text-[14px]">
                  {placeholder}
                </div>
              }
              className={combinedSelectClassName}
              onChange={disabled ? null : onChange}
              onDeselect={disabled ? null : onDeselect}
              onBlur={disabled ? null : handleBlur}
              suffixIcon={suffixIcon ? <div className="flex items-center pointer-events-none">{suffixIcon}</div> : null}
              mode={mode ? mode : (multiple === true ? "multiple" : undefined)}
              defaultValue={defaultValue}
              open={disabled ? false : undefined}
              onMouseDown={(e) => {
                if (disabled) {
                  e.preventDefault();
                  e.stopPropagation();
                  return;
                }
                if (onClick) onClick(e);
              }}
              {...(mode === "tags"
                ? {}
                : { options: (options || []).map((o) => ({ ...o, value: String(o.value) })) })}
              {...rest}
            >
              {mode === "tags" && renderOptions()}
            </Select>
          </WrapperComponent>
        )}
        {error && touched ? (
          <div className="mt-1 w-full text-start">
            <span className={`text-red-600 text-[12px] font-[500] ${errorClassName}`}>
              {error}
            </span>
          </div>
        ) : null}
      </div>
    </div>
  );
};

export default Selector;